import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { 
  X, Upload, FileSpreadsheet, Image as ImageIcon, Sparkles, 
  CheckCircle2, AlertTriangle, ArrowRight, Loader2, RefreshCw, FileText 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { generateContentWithFallback } from '@/lib/geminiClient';

const fmt = (n) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(n);

export default function ClientPOImportModal({ 
  isOpen, 
  onClose, 
  activeBrand, 
  brandCatalog = [], 
  onApplyItems 
}) {
  const [activeTab, setActiveTab] = useState('excel'); // 'excel' | 'image' | 'paste'
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedRows, setParsedRows] = useState([]);
  const [rawPastedText, setRawPastedText] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  if (!isOpen) return null;

  // Helper matching row with brandCatalog
  const matchWithCatalog = (rawName, rawSize, rawQty) => {
    const cleanRaw = String(rawName || '').trim().toLowerCase();
    const qty = parseInt(rawQty) > 0 ? parseInt(rawQty) : 1;
    const sizeNum = parseFloat(String(rawSize).replace(/[^\d.]/g, '')) || 0;

    // Try finding in brandCatalog
    // brandCatalog items are { id, nama_product, sizes: [ { size, botol_id, racikan, ... } ] }
    let matchedVariant = null;
    let matchedSize = null;

    // 1. Exact or partial name match
    matchedVariant = brandCatalog.find(b => {
      const bName = (b.nama_product || '').toLowerCase();
      // Remove brand prefix like "kokulu - "
      const shortName = bName.replace(new RegExp(`^${(activeBrand || '').toLowerCase()}\\s*[-–:]*\\s*`), '');
      return bName === cleanRaw || shortName === cleanRaw || bName.includes(cleanRaw) || cleanRaw.includes(shortName);
    });

    if (matchedVariant && matchedVariant.sizes?.length > 0) {
      if (sizeNum > 0) {
        matchedSize = matchedVariant.sizes.find(s => parseFloat(s.size) === sizeNum);
      }
      if (!matchedSize) {
        matchedSize = matchedVariant.sizes[0];
      }
    }

    return {
      rawName,
      rawSize: sizeNum || (matchedSize?.size) || 30,
      qty,
      matched: !!matchedVariant,
      variant: matchedVariant,
      sizeObj: matchedSize,
      unitPrice: matchedSize?.harga_jual_per_pcs || 0
    };
  };

  // Handle Excel File Upload
  const handleExcelUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (!rows || rows.length <= 1) {
          toast.error('File Excel kosong atau hanya memiliki 1 baris header.');
          setIsProcessing(false);
          return;
        }

        // Find header columns index
        let headerRowIdx = 0;
        let nameCol = 0;
        let sizeCol = -1;
        let qtyCol = 1;

        // Scan first 5 rows for header keyword
        for (let i = 0; i < Math.min(5, rows.length); i++) {
          const r = rows[i];
          if (!Array.isArray(r)) continue;
          r.forEach((cell, cIdx) => {
            const str = String(cell || '').toLowerCase();
            if (str.includes('varian') || str.includes('produk') || str.includes('nama') || str.includes('item')) {
              headerRowIdx = i;
              nameCol = cIdx;
            }
            if (str.includes('ukuran') || str.includes('size') || str.includes('ml')) {
              sizeCol = cIdx;
            }
            if (str.includes('qty') || str.includes('jumlah') || str.includes('pcs') || str.includes('kuantitas')) {
              qtyCol = cIdx;
            }
          });
        }

        const parsed = [];
        for (let i = headerRowIdx + 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || !row[nameCol]) continue;
          const name = String(row[nameCol]).trim();
          if (!name) continue;

          const size = sizeCol >= 0 ? row[sizeCol] : 30;
          const qty = row[qtyCol] || 1;

          parsed.push(matchWithCatalog(name, size, qty));
        }

        setParsedRows(parsed);
        toast.success(`Berhasil membaca ${parsed.length} item dari Excel!`);
      } catch (err) {
        console.error('Gagal memproses file Excel:', err);
        toast.error('Gagal membaca file Excel. Pastikan format file benar (.xlsx / .csv).');
      } finally {
        setIsProcessing(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Handle Raw Pasted Text (from Sheets or Clipboard)
  const handleParsePastedText = () => {
    if (!rawPastedText.trim()) {
      toast.error('Silakan tempel teks tabel terlebih dahulu.');
      return;
    }

    const lines = rawPastedText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const parsed = [];

    lines.forEach(line => {
      // Split by tab, semicolon, or comma
      let parts = line.split('\t');
      if (parts.length === 1) parts = line.split(';');
      if (parts.length === 1) parts = line.split(',');

      if (parts.length >= 1) {
        const name = parts[0]?.trim();
        if (!name) return;
        let size = 30;
        let qty = 1;

        if (parts.length === 2) {
          // Check if second column is qty or size
          const val = parts[1].trim();
          if (val.toLowerCase().includes('ml')) {
            size = val;
          } else {
            qty = val;
          }
        } else if (parts.length >= 3) {
          size = parts[1]?.trim();
          qty = parts[2]?.trim();
        }

        parsed.push(matchWithCatalog(name, size, qty));
      }
    });

    setParsedRows(parsed);
    toast.success(`Berhasil mengurai ${parsed.length} baris!`);
  };

  // Handle Image Upload & AI Vision Parsing
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show Preview
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    setIsProcessing(true);
    try {
      // Convert to base64
      const base64Data = await new Promise((resolve) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result.split(',')[1]);
        r.readAsDataURL(file);
      });

      const imagePart = {
        inlineData: {
          data: base64Data,
          mimeType: file.type || 'image/jpeg'
        }
      };

      const prompt = `Anda adalah asisten data entry pabrik maklon parfum Satala Dermatech. 
Analisis gambar nota / PO / tulisan / tabel pesanan ini dan ekstrak daftar produk/varian, ukuran kemasan (ml), dan jumlah pesanan (pcs).
Brand aktif saat ini: "${activeBrand || 'Umum'}".

Format kembalian WAJIB HANYA berupa JSON valid tanpa markdown penutup tambahan, dengan struktur array of object:
[
  {
    "nama_varian": "Nama Varian / Produk",
    "ukuran_ml": 30,
    "qty": 50
  }
]`;

      const response = await generateContentWithFallback(
        "Ekstrak item pesanan dari gambar nota atau dokumen secara akurat.",
        [imagePart, prompt]
      );

      let text = response?.text || (typeof response === 'string' ? response : '');
      text = text.replace(/```json/gi, '').replace(/```/g, '').trim();

      const jsonStart = text.indexOf('[');
      const jsonEnd = text.lastIndexOf(']');
      if (jsonStart !== -1 && jsonEnd !== -1) {
        text = text.substring(jsonStart, jsonEnd + 1);
      }

      const extracted = JSON.parse(text);
      if (Array.isArray(extracted) && extracted.length > 0) {
        const parsed = extracted.map(item => 
          matchWithCatalog(item.nama_varian, item.ukuran_ml, item.qty)
        );
        setParsedRows(parsed);
        toast.success(`AI Vision berhasil menemukan ${parsed.length} item dari gambar!`);
      } else {
        toast.warning('Tidak ada data pesanan yang terbaca jelas dari gambar.');
      }
    } catch (err) {
      console.error('Error parsing image with AI Vision:', err);
      toast.error('Gagal memproses gambar dengan AI. Pastikan tulisan pada foto terlihat jelas.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Apply parsed items into the parent PO Form
  const handleApply = () => {
    if (parsedRows.length === 0) {
      toast.error('Belum ada item pesanan untuk diterapkan.');
      return;
    }

    const newProducts = parsedRows.map(row => {
      const v = row.variant;
      const s = row.sizeObj;

      if (v && s) {
        return {
          variant_id: v.id,
          nama_product: v.nama_product,
          qty: row.qty,
          selected_size: s.size,
          botol_id: s.botol_id || null,
          tutup_id: s.tutup_id || null,
          spray_id: s.spray_id || null,
          botol_dari_client: s.botol_dari_client ?? true,
          botol_ukuran_client: s.botol_ukuran_client || parseFloat(s.size) || 30,
          racikan: s.racikan || [],
          komponen_tambahan: s.komponen_tambahan || [],
          harga_jual_per_pcs: row.unitPrice || 0,
          total_harga_jual: (row.unitPrice || 0) * row.qty,
          jasa_nominal: 4000
        };
      } else {
        // Unmatched fallback item
        return {
          nama_product: row.rawName ? `${activeBrand ? activeBrand + ' - ' : ''}${row.rawName}` : 'Varian Baru',
          qty: row.qty,
          botol_id: null,
          tutup_id: null,
          spray_id: null,
          botol_dari_client: true,
          botol_ukuran_client: row.rawSize || 30,
          racikan: [],
          komponen_tambahan: [],
          harga_jual_per_pcs: 0,
          total_harga_jual: 0,
          jasa_nominal: 4000
        };
      }
    });

    onApplyItems(newProducts);
    toast.success(`${newProducts.length} item pesanan berhasil dimasukkan ke form PO!`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-[#E6D5B8] flex flex-col max-h-[90vh] overflow-hidden">
        
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-[#E6D5B8] bg-gradient-to-r from-[#FFFDF9] to-[#F9F5EC] flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#8C6208]" />
              <h3 className="text-lg font-black text-[#302621]">Auto-Generate Pesanan</h3>
              {activeBrand && (
                <span className="bg-[#8C6208]/10 text-[#8C6208] text-xs font-bold px-2.5 py-0.5 rounded-full border border-[#8C6208]/20">
                  Brand: {activeBrand}
                </span>
              )}
            </div>
            <p className="text-xs text-[#7A695B] mt-0.5">
              Ekstrak daftar pesanan otomatis dari Excel, Spreadsheet, atau Foto Dokumen/Nota.
            </p>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose} 
            className="rounded-full w-9 h-9 text-[#7A695B] hover:text-[#302621] hover:bg-[#E6D5B8]/40"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-[#E6D5B8] bg-[#F9F5EC]/50 px-6 pt-3 gap-2">
          <button
            onClick={() => setActiveTab('excel')}
            className={`pb-3 px-4 text-xs font-bold transition-all border-b-2 flex items-center gap-2 ${
              activeTab === 'excel'
                ? 'border-[#8C6208] text-[#8C6208]'
                : 'border-transparent text-[#7A695B] hover:text-[#302621]'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4" />
            File Excel (.xlsx / .csv)
          </button>
          <button
            onClick={() => setActiveTab('paste')}
            className={`pb-3 px-4 text-xs font-bold transition-all border-b-2 flex items-center gap-2 ${
              activeTab === 'paste'
                ? 'border-[#8C6208] text-[#8C6208]'
                : 'border-transparent text-[#7A695B] hover:text-[#302621]'
            }`}
          >
            <FileText className="w-4 h-4" />
            Copy-Paste Spreadsheet
          </button>
          <button
            onClick={() => setActiveTab('image')}
            className={`pb-3 px-4 text-xs font-bold transition-all border-b-2 flex items-center gap-2 ${
              activeTab === 'image'
                ? 'border-[#8C6208] text-[#8C6208]'
                : 'border-transparent text-[#7A695B] hover:text-[#302621]'
            }`}
          >
            <ImageIcon className="w-4 h-4" />
            Foto / Gambar (AI Vision)
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          
          {/* TAB 1: EXCEL FILE */}
          {activeTab === 'excel' && (
            <div className="space-y-4">
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-[#D4AF37]/60 hover:border-[#8C6208] hover:bg-[#F9F5EC]/40 rounded-2xl p-8 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-3 group"
              >
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleExcelUpload} 
                  accept=".xlsx, .xls, .csv" 
                  className="hidden" 
                />
                <div className="w-14 h-14 rounded-2xl bg-[#F9F5EC] border border-[#E6D5B8] flex items-center justify-center text-[#8C6208] group-hover:scale-110 transition-transform">
                  <FileSpreadsheet className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#302621]">Klik untuk unggah file Excel atau CSV</p>
                  <p className="text-xs text-[#7A695B] mt-1">Mendukung format .xlsx, .xls, dan .csv</p>
                </div>
                <span className="text-[11px] bg-[#8C6208]/10 text-[#8C6208] px-3 py-1 rounded-full font-semibold">
                  Kolom otomatis: Nama Varian, Ukuran (ml), Qty (pcs)
                </span>
              </div>
            </div>
          )}

          {/* TAB 2: COPY PASTE SPREADSHEET */}
          {activeTab === 'paste' && (
            <div className="space-y-3">
              <label className="text-xs font-bold text-[#302621] block">
                Tempel (Paste) data tabel dari Google Sheets / Excel:
              </label>
              <textarea
                value={rawPastedText}
                onChange={(e) => setRawPastedText(e.target.value)}
                placeholder={"Contoh:\nDior Sauvage\t50\t100\nBlack Opium\t30\t50\nNagita Slavina\t100\t200"}
                rows={5}
                className="w-full text-xs font-mono p-3 rounded-xl border border-[#E6D5B8] focus:outline-none focus:ring-2 focus:ring-[#8C6208]/40 bg-[#FFFDF9]"
              />
              <Button 
                onClick={handleParsePastedText}
                className="bg-[#8C6208] hover:bg-[#6F4E06] text-white text-xs font-bold h-9 px-4 rounded-xl shadow-sm"
              >
                Urai Teks Tabel
              </Button>
            </div>
          )}

          {/* TAB 3: IMAGE / AI VISION */}
          {activeTab === 'image' && (
            <div className="space-y-4">
              <div 
                onClick={() => imageInputRef.current?.click()}
                className="border-2 border-dashed border-[#D4AF37]/60 hover:border-[#8C6208] hover:bg-[#F9F5EC]/40 rounded-2xl p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-3 group"
              >
                <input 
                  type="file" 
                  ref={imageInputRef} 
                  onChange={handleImageUpload} 
                  accept="image/*" 
                  className="hidden" 
                />
                <div className="w-12 h-12 rounded-2xl bg-[#F9F5EC] border border-[#E6D5B8] flex items-center justify-center text-[#8C6208] group-hover:scale-110 transition-transform">
                  <ImageIcon className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#302621]">Unggah foto nota, formulir, atau invoice fisik</p>
                  <p className="text-xs text-[#7A695B] mt-1">AI Vision akan mendeteksi daftar produk dan jumlahnya otomatis</p>
                </div>
              </div>

              {imagePreview && (
                <div className="relative rounded-2xl overflow-hidden border border-[#E6D5B8] max-h-48 flex items-center justify-center bg-black/5">
                  <img src={imagePreview} alt="Preview" className="max-h-48 object-contain" />
                </div>
              )}
            </div>
          )}

          {/* Loading Spinner */}
          {isProcessing && (
            <div className="py-8 flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 text-[#8C6208] animate-spin" />
              <p className="text-xs font-bold text-[#8C6208]">Memproses dan mencocokkan dengan database brand...</p>
            </div>
          )}

          {/* PARSED PREVIEW TABLE */}
          {!isProcessing && parsedRows.length > 0 && (
            <div className="border border-[#E6D5B8] rounded-2xl overflow-hidden shadow-sm bg-white">
              <div className="px-4 py-3 bg-[#F9F5EC] border-b border-[#E6D5B8] flex items-center justify-between">
                <span className="text-xs font-bold text-[#302621] uppercase tracking-wider">
                  Hasil Deteksi ({parsedRows.length} Item)
                </span>
                <span className="text-[11px] font-semibold text-[#8C6208]">
                  {parsedRows.filter(r => r.matched).length} Cocok di Database
                </span>
              </div>
              <div className="max-h-60 overflow-y-auto divide-y divide-gray-100 text-xs">
                {parsedRows.map((r, idx) => (
                  <div key={idx} className="p-3 flex items-center justify-between hover:bg-[#FFFDF9]">
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center font-bold text-[10px] text-slate-500">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-bold text-[#302621]">
                          {r.variant?.nama_product || r.rawName}
                        </p>
                        <p className="text-[11px] text-[#7A695B]">
                          Ukuran: <strong className="text-[#302621]">{r.rawSize} ml</strong> • Qty: <strong className="text-[#302621]">{r.qty} pcs</strong>
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      {r.matched ? (
                        <div className="flex items-center gap-1.5 text-green-600 font-bold text-[11px]">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Cocok Database</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-amber-600 font-bold text-[11px]">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span>Varian Baru</span>
                        </div>
                      )}
                      {r.unitPrice > 0 && (
                        <p className="text-[10px] text-[#8C6208] font-bold">
                          @ Rp {fmt(r.unitPrice)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-[#E6D5B8] bg-[#F9F5EC]/60 flex items-center justify-between">
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="border-[#E6D5B8] text-[#7A695B] hover:bg-white text-xs font-bold rounded-xl"
          >
            Batal
          </Button>

          <Button
            onClick={handleApply}
            disabled={parsedRows.length === 0 || isProcessing}
            className="bg-[#302621] hover:bg-[#1A1411] text-white font-bold text-xs px-6 h-10 rounded-xl shadow-md gap-2 disabled:opacity-50"
          >
            <span>Terapkan ke Pesanan ({parsedRows.length})</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

      </div>
    </div>
  );
}
